/**
 * Review Dashboard
 * 
 * Comprehensive overview of review status with integration of all review panels.
 * Provides quick actions and visualizations.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { 
  FileCheck, 
  Users, 
  FileEdit, 
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';
import { 
  getSubmissionStatus,
  getReviewAssignments,
  getRevisionSummary,
  type ReviewSubmissionStatus
} from '../../api/reviewApi';
import { getReport } from '../../api/reports';
import { ReviewSubmissionPanel } from './ReviewSubmissionPanel';
import { RevisionRequestsPanel } from './RevisionRequestsPanel';
import { EnhancedCommentsPanel } from './EnhancedCommentsPanel';
import { ReviewHistoryPanel } from './ReviewHistoryPanel';
import { QAScoreReadinessPanel, FeedbackPlanPreviewModal } from '../QA';

export interface ReviewDashboardProps {
  reportId: string;
  onStatusChange?: () => void;
  /** When set, review submission panel can offer "Go to QA tab" after pre-submission validation errors */
  onSwitchToQATab?: () => void;
  /** Called when a proposal is selected for detailed review */
  onProposalSelect?: (proposalId: string) => void;
}

export function ReviewDashboard({ reportId, onStatusChange, onSwitchToQATab, onProposalSelect }: ReviewDashboardProps) {
  // Get submission status
  const { data: submissionStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['review-submission-status', reportId],
    queryFn: () => getSubmissionStatus(reportId),
    refetchOnWindowFocus: false,
  });

  // Get assignments
  const { data: assignments } = useQuery({
    queryKey: ['review-assignments', reportId],
    queryFn: () => getReviewAssignments(reportId),
    refetchOnWindowFocus: false,
  });

  // Get revision summary
  const { data: revisionSummary } = useQuery({
    queryKey: ['revision-summary', reportId],
    queryFn: () => getRevisionSummary(reportId),
    refetchOnWindowFocus: false,
  });

  const { data: qaReportSnap } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    staleTime: 30_000,
  });

  const [feedbackPlanOpen, setFeedbackPlanOpen] = useState(false);
  const [fbAutoCompute, setFbAutoCompute] = useState(true);
  const [fbExpandPrompt, setFbExpandPrompt] = useState(false);

  if (statusLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading review dashboard...</span>
        </div>
      </Card>
    );
  }

  const activeAssignments = assignments?.filter(a => a.status === 'pending' || a.status === 'in_progress') || [];
  const pendingRevisions = revisionSummary?.completion_status.total_pending || 0;
  const criticalRevisions = revisionSummary?.completion_status.critical_pending || 0;

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Submission Status</p>
              <p className="text-lg font-semibold">
                {submissionStatus?.submitted ? (
                  <Badge variant="success">Submitted</Badge>
                ) : (
                  <Badge variant="default">Not Submitted</Badge>
                )}
              </p>
            </div>
            <FileCheck className="h-8 w-8 text-primary-500 opacity-50" />
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Active Reviews</p>
              <p className="text-lg font-semibold">{activeAssignments.length}</p>
            </div>
            <Users className="h-8 w-8 text-blue-500 opacity-50" />
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Pending Revisions</p>
              <p className="text-lg font-semibold">
                {criticalRevisions > 0 ? (
                  <Badge variant="error">{criticalRevisions} Critical</Badge>
                ) : pendingRevisions > 0 ? (
                  <Badge variant="warning">{pendingRevisions}</Badge>
                ) : (
                  <Badge variant="success">0</Badge>
                )}
              </p>
            </div>
            <FileEdit className="h-8 w-8 text-orange-500 opacity-50" />
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Revision Cycles</p>
              <p className="text-lg font-semibold">{submissionStatus?.revision_cycle_count || 0}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
          </div>
        </Card>
      </div>

      {/* Quick Status Overview */}
      {submissionStatus && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">Quick Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Ready for Submission</p>
              {submissionStatus.ready_for_submission ? (
                <Badge variant="success" className="flex items-center gap-1 w-fit">
                  <CheckCircle className="h-3 w-3" />
                  Yes
                </Badge>
              ) : (
                <Badge variant="error" className="flex items-center gap-1 w-fit">
                  <AlertTriangle className="h-3 w-3" />
                  No
                </Badge>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Ready for Re-submission</p>
              {submissionStatus.ready_for_re_submission ? (
                <Badge variant="success" className="flex items-center gap-1 w-fit">
                  <CheckCircle className="h-3 w-3" />
                  Yes
                </Badge>
              ) : (
                <Badge variant="warning" className="flex items-center gap-1 w-fit">
                  <AlertTriangle className="h-3 w-3" />
                  No
                </Badge>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Assignments</p>
              <p className="text-sm font-medium">{submissionStatus.total_assignments}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Active Assignments</p>
              <p className="text-sm font-medium">{submissionStatus.active_assignments}</p>
            </div>
          </div>
        </Card>
      )}

      <QAScoreReadinessPanel
        reportId={reportId}
        executiveSummaryVersionLabel={
          typeof qaReportSnap?.meta?.current_version === 'number'
            ? `v${qaReportSnap.meta.current_version}`
            : undefined
        }
        onBelowTargetFeedbackStep1={() => {
          setFbAutoCompute(true);
          setFbExpandPrompt(false);
          setFeedbackPlanOpen(true);
        }}
        onBelowTargetFeedbackStep2={() => {
          setFbAutoCompute(false);
          setFbExpandPrompt(true);
          setFeedbackPlanOpen(true);
        }}
      />

      <FeedbackPlanPreviewModal
        reportId={reportId}
        open={feedbackPlanOpen}
        onClose={() => setFeedbackPlanOpen(false)}
        autoComputeOnOpen={fbAutoCompute}
        expandPromptPreviewOnOpen={fbExpandPrompt}
      />

      {/* Review Panels */}
      <ReviewSubmissionPanel 
        reportId={reportId} 
        onSubmissionChange={onStatusChange}
        onSwitchToQATab={onSwitchToQATab}
      />
      
      <RevisionRequestsPanel 
        reportId={reportId} 
        onRevisionChange={onStatusChange}
      />
      
      <EnhancedCommentsPanel 
        reportId={reportId} 
        onCommentChange={onStatusChange}
      />
      
      <ReviewHistoryPanel reportId={reportId} />
    </div>
  );
}
